import { BigMath } from "./math";

/**
 * 构造按竞猜聚合维度取消订单的参数 (orderNoType: 2)
 * 处理 betType/guessType 和 price/orderPrice 的字段兼容性
 */
export function formatDimensionCancelParams( order: any, guessNo: string ) {
    return {
        orderNo: guessNo,
        orderNoType: 2 as const,
        guessType: ( order.betType ?? order.guessType ) as 1 | 2,
        direction: order.direction as 1 | 2,
        price: order.price ?? order.orderPrice,
    };
}

export type TraditionalOrderBookSide = "yes" | "no";

export interface TraditionalOrderBookInputOrder {
    price: number | string;
    totalQuantity: number;
    totalAmount?: number | string;
}

export interface TraditionalOrderBookDataParams {
    side: TraditionalOrderBookSide;
    yesSellOrders?: TraditionalOrderBookInputOrder[];
    noSellOrders?: TraditionalOrderBookInputOrder[];
    yesBuyOrders?: TraditionalOrderBookInputOrder[];
    noBuyOrders?: TraditionalOrderBookInputOrder[];
    yesPrice?: number | string;
    noPrice?: number | string;
}

export interface TraditionalOrderBookLevel {
    price: number;
    quantity: number;
    totalAmount: number;
    depthPercent: number;
}

export interface TraditionalOrderBookData {
    side: TraditionalOrderBookSide;
    asks: TraditionalOrderBookLevel[];
    lastPrice: number;
    bids: TraditionalOrderBookLevel[];
}

const TRADITIONAL_ORDER_BOOK_VISIBLE_LEVELS = 5;
const EMPTY_TRADITIONAL_ORDER_BOOK_LEVEL: TraditionalOrderBookLevel = {
    price: 0,
    quantity: 0,
    totalAmount: 0,
    depthPercent: 0,
};

function toTraditionalOrderBookLevels( orders: TraditionalOrderBookInputOrder[] = [] ): TraditionalOrderBookLevel[] {
    const visibleOrders = orders.map( ( order ) => {
        const price = Number( order.price );
        const quantity = Number( order.totalQuantity );
        const rawTotalAmount = Number( order.totalAmount );
        const totalAmount = Number.isFinite( rawTotalAmount ) ? rawTotalAmount : BigMath.times( price, quantity );

        return { price, quantity, totalAmount };
    } ).filter( ( order ) =>
        Number.isFinite( order.price )
        && Number.isFinite( order.quantity )
        && Number.isFinite( order.totalAmount )
        && BigMath.gt( order.price, 0 )
        && BigMath.gt( order.quantity, 0 )
    ).sort( ( a, b ) => {
        if ( BigMath.gt( b.price, a.price ) ) return 1;
        if ( BigMath.lt( b.price, a.price ) ) return -1;
        return 0;
    } ).slice( 0, TRADITIONAL_ORDER_BOOK_VISIBLE_LEVELS );

    const maxQuantity = visibleOrders.reduce(
        ( max, order ) => ( BigMath.gt( order.quantity, max ) ? order.quantity : max ),
        0,
    );

    const levels = visibleOrders.map( ( order ) => ( {
        ...order,
        depthPercent: BigMath.gt( maxQuantity, 0 )
            ? BigMath.times( BigMath.div( order.quantity, maxQuantity ), 100 )
            : 0,
    } ) );

    return [
        ...levels,
        ...Array( Math.max( 0, TRADITIONAL_ORDER_BOOK_VISIBLE_LEVELS - levels.length ) ).fill(
            EMPTY_TRADITIONAL_ORDER_BOOK_LEVEL,
        ),
    ];
}

function resolveTraditionalLastPrice( price: number | string | undefined ) {
    const normalizedPrice = Number( price );
    return Number.isFinite( normalizedPrice ) ? normalizedPrice : 0;
}

export function getTraditionalOrderBookData( params: TraditionalOrderBookDataParams ): TraditionalOrderBookData {
    const sellOrders = params.side === "yes" ? params.yesSellOrders : params.noSellOrders;
    const buyOrders = params.side === "yes" ? params.yesBuyOrders : params.noBuyOrders;
    const lastPrice = params.side === "yes" ? params.yesPrice : params.noPrice;

    return {
        side: params.side,
        asks: toTraditionalOrderBookLevels( sellOrders ),
        lastPrice: resolveTraditionalLastPrice( lastPrice ),
        bids: toTraditionalOrderBookLevels( buyOrders ),
    };
}
